/**
 * Public station micro-site — /r/:slug
 *
 * Renders a public-facing hero, live player, latest news, latest podcasts, and
 * social links for a single radio station. Slug can be either the station_id
 * (e.g. `stn_abc...`) or a name-derived slug (`seductive-vibes-radio`).
 *
 * No authentication required — this is the public face of a station.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Radio, Play, Pause, Volume2, ExternalLink, Headphones, Newspaper, Mic,
  Facebook, Instagram, Twitter, Youtube, Linkedin, Music,
  QrCode, Download, Copy, Check, Image as ImageIcon, Printer,
} from "lucide-react";
import { getStationTheme } from "@/lib/stationTheme";
import { appPrompt } from "@/components/AppDialogHost";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// Header (sticky)
// ─────────────────────────────────────────────────────────────────────────────
function StationHeader({ station, theme }) {
  return (
    <header className="sticky top-0 z-30 bg-[#0a0d14]/95 backdrop-blur-md" style={{ borderBottom: "0.5px solid #1e2130" }}>
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
        <Link to={`/r/${station.slug || station.station_id}`} className="flex items-center gap-3 group" data-testid="public-station-brand">
          {station.logo_url ? (
            <img
              src={station.logo_url}
              alt={station.name}
              className="h-10 w-10 rounded-lg object-cover"
              style={{ ...theme.ringStyle }}
            />
          ) : (
            <div className="h-10 w-10 rounded-lg flex items-center justify-center" style={{ ...theme.tintStyle, color: theme.hex }}>
              <Radio className="h-5 w-5" />
            </div>
          )}
          <div className="min-w-0">
            <div className="text-[14px] font-semibold text-[#c8ccd8] truncate leading-tight">{station.name}</div>
            <div className="text-[11px] uppercase tracking-wider" style={{ color: theme.hex }}>{theme.label}</div>
          </div>
        </Link>
        <Link
          to="/"
          className="hidden sm:inline-flex items-center gap-1.5 text-[11px] text-[#4a5068] hover:text-[#c8ccd8] transition"
          data-testid="public-station-platform-link"
        >
          Powered by BroadcastFlow AI <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero with live player
// ─────────────────────────────────────────────────────────────────────────────
function Hero({ station, theme }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [vol, setVol] = useState(0.8);
  const [err, setErr] = useState("");

  const streamUrl = station.stream?.https || station.stream?.shoutcast || station.stream?.icecast || station.stream?.backup || "";

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = vol;
  }, [vol]);

  const toggle = async () => {
    if (!audioRef.current) return;
    setErr("");
    try {
      if (playing) {
        audioRef.current.pause();
        setPlaying(false);
      } else {
        await audioRef.current.play();
        setPlaying(true);
      }
    } catch (e) {
      setErr("Stream is offline or blocked by your browser.");
      setPlaying(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden"
      data-testid="public-station-hero"
      style={{
        background: `radial-gradient(circle at 20% 30%, ${theme.hex}22 0%, transparent 55%), radial-gradient(circle at 90% 80%, ${theme.hex}11 0%, transparent 50%), #0a0d14`,
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-12 sm:py-16 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        {/* Left: station identity */}
        <div>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold mb-5"
            style={{ ...theme.tintStyle, ...theme.borderStyle, borderWidth: "0.5px", borderStyle: "solid", color: theme.hex }}
            data-testid="public-station-genre-pill"
          >
            <span className="h-2 w-2 rounded-full animate-pulse" style={theme.dotStyle} />
            On Air · {(station.genre || theme.label).split(",").map((s) => s.trim()).filter(Boolean).join(" · ")}
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#f0f2f8] tracking-tight leading-[1.05]">
            {station.name}
          </h1>
          {station.founding?.badge && (
            <div className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-300"
                 data-testid="public-founding-badge">
              ★ BroadcastFlow Founding Tester #{station.founding.cohort}
            </div>
          )}
          {station.tagline && (
            <p className="mt-4 text-lg text-[#a0a8bd] max-w-2xl leading-relaxed">{station.tagline}</p>
          )}
          {station.description && (
            <p className="mt-3 text-sm text-[#6b7280] max-w-2xl leading-relaxed">{station.description}</p>
          )}

          {/* Listen Live block */}
          <div
            className="mt-8 bg-[#0a0d14] rounded-2xl p-5 flex items-center gap-5 max-w-md"
            style={{ border: "0.5px solid #1e2130", ...theme.glowStyle }}
            data-testid="public-station-player"
          >
            <button
              type="button"
              onClick={toggle}
              data-testid="player-toggle-btn"
              disabled={!streamUrl}
              className="h-14 w-14 rounded-full flex items-center justify-center text-[#0a0d14] font-bold shadow-lg shrink-0 transition disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ backgroundColor: theme.hex }}
              aria-label={playing ? "Pause" : "Play"}
            >
              {playing ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6 ml-0.5" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: theme.hex }}>
                {streamUrl ? (playing ? "Now Streaming" : "Listen Live") : "Stream Offline"}
              </div>
              <div className="text-[13px] text-[#c8ccd8] font-medium mt-0.5 truncate">{station.name}</div>
              <div className="mt-2 flex items-center gap-2">
                <Volume2 className="h-3.5 w-3.5 text-[#4a5068]" />
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={vol}
                  onChange={(e) => setVol(Number(e.target.value))}
                  data-testid="player-volume"
                  className="flex-1 h-1 rounded-full appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(90deg, ${theme.hex} 0%, ${theme.hex} ${vol * 100}%, #1e2130 ${vol * 100}%, #1e2130 100%)`,
                  }}
                />
              </div>
              {err && <div className="mt-2 text-[10px] text-[#ff6b6b]">{err}</div>}
            </div>
            {streamUrl && (
              <audio
                ref={audioRef}
                src={streamUrl}
                preload="none"
                onEnded={() => setPlaying(false)}
                onError={() => { setErr("Stream is offline or blocked by your browser."); setPlaying(false); }}
              />
            )}
          </div>
        </div>

        {/* Right: big circular logo / vinyl */}
        <div className="flex items-center justify-center lg:justify-end">
          <div
            className="relative h-64 w-64 sm:h-80 sm:w-80 rounded-full flex items-center justify-center"
            style={{
              background: `radial-gradient(circle, ${theme.hex}26 0%, transparent 70%)`,
            }}
          >
            <div
              className="absolute inset-4 rounded-full"
              style={{ border: `1px solid ${theme.hex}44` }}
            />
            <div
              className="absolute inset-10 rounded-full"
              style={{ border: `1px solid ${theme.hex}22` }}
            />
            {station.logo_url ? (
              <img
                src={station.logo_url}
                alt={station.name}
                className="h-44 w-44 sm:h-56 sm:w-56 rounded-full object-cover relative z-10"
                style={theme.glowStyle}
              />
            ) : (
              <div
                className="h-44 w-44 sm:h-56 sm:w-56 rounded-full flex items-center justify-center relative z-10"
                style={{ ...theme.tintStyle, color: theme.hex, ...theme.glowStyle }}
              >
                <Radio className="h-20 w-20" />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// News section
// ─────────────────────────────────────────────────────────────────────────────
function NewsSection({ items, theme }) {
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 py-12 sm:py-16" data-testid="public-station-news-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ ...theme.tintStyle, color: theme.hex }}>
          <Newspaper className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a5068] font-semibold">// On the Newsroom</div>
          <h2 className="text-xl font-semibold text-[#c8ccd8] tracking-tight">Latest news</h2>
        </div>
      </div>
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((n) => (
          <a
            key={n.story_id || n.title}
            href={n.link || "#"}
            target={n.link ? "_blank" : undefined}
            rel="noopener noreferrer"
            className="group bg-[#0a0d14] rounded-xl overflow-hidden hover:scale-[1.02] transition-transform"
            style={{ border: "0.5px solid #1e2130" }}
            data-testid={`public-news-card-${n.story_id || n.title}`}
          >
            {n.image_url && (
              <div className="h-40 bg-[#0f1117] overflow-hidden">
                <img
                  src={n.image_url}
                  alt={n.title}
                  loading="lazy"
                  className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            )}
            <div className="p-4">
              {(n.category || n.source) && (
                <div className="flex items-center gap-2 mb-2">
                  {n.category && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded"
                      style={{ ...theme.tintStyle, color: theme.hex }}
                    >
                      {n.category}
                    </span>
                  )}
                  {n.source && (
                    <span className="text-[10px] text-[#4a5068] uppercase">{n.source}</span>
                  )}
                </div>
              )}
              <h3 className="text-[14px] font-semibold text-[#c8ccd8] leading-snug line-clamp-2 group-hover:text-white transition-colors">
                {n.title}
              </h3>
              {n.summary && (
                <p className="mt-2 text-[12px] text-[#6b7280] leading-relaxed line-clamp-3">{n.summary}</p>
              )}
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Podcasts section
// ─────────────────────────────────────────────────────────────────────────────
function fmtDuration(sec) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function PodcastsSection({ items, theme }) {
  if (!items.length) return null;
  return (
    <section className="max-w-7xl mx-auto px-6 py-12 sm:py-16" data-testid="public-station-podcasts-section">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg flex items-center justify-center" style={{ ...theme.tintStyle, color: theme.hex }}>
          <Mic className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a5068] font-semibold">// Recent Episodes</div>
          <h2 className="text-xl font-semibold text-[#c8ccd8] tracking-tight">Podcasts</h2>
        </div>
      </div>
      <ul className="space-y-3">
        {items.map((p) => (
          <li
            key={p.episode_id}
            className="bg-[#0a0d14] rounded-xl p-4 sm:p-5"
            style={{ border: "0.5px solid #1e2130" }}
            data-testid={`public-podcast-${p.episode_id}`}
          >
            <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">
              {p.cover_url ? (
                <img src={p.cover_url} alt={p.title} className="h-16 w-16 rounded-lg object-cover shrink-0" />
              ) : (
                <div
                  className="h-16 w-16 rounded-lg flex items-center justify-center shrink-0"
                  style={{ ...theme.tintStyle, color: theme.hex }}
                >
                  <Headphones className="h-6 w-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {p.episode_number > 0 && (
                    <span
                      className="text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded"
                      style={{ ...theme.tintStyle, color: theme.hex }}
                    >
                      EP {p.episode_number}
                    </span>
                  )}
                  {p.duration_sec > 0 && (
                    <span className="text-[10px] text-[#4a5068] uppercase">{fmtDuration(p.duration_sec)}</span>
                  )}
                </div>
                <h3 className="mt-1 text-[14px] sm:text-[15px] font-semibold text-[#c8ccd8] leading-tight">{p.title}</h3>
                {p.description && (
                  <p className="mt-1.5 text-[12px] text-[#6b7280] leading-relaxed line-clamp-2">{p.description}</p>
                )}
                {p.audio_url && (
                  <audio
                    src={p.audio_url}
                    controls
                    preload="none"
                    className="mt-3 w-full max-w-md h-10"
                    style={{ filter: "invert(0.85) hue-rotate(180deg)" }}
                  />
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Share / QR section — shareable QR + branded posters for flyers, merch, IG
// ─────────────────────────────────────────────────────────────────────────────
const POSTER_FORMATS = [
  { id: "a4",     label: "Flyer (A4 print)",   ratio: "1 / 1.414", source: "flyer",  icon: Printer },
  { id: "square", label: "Square (Instagram)", ratio: "1 / 1",     source: "ig-feed", icon: ImageIcon },
  { id: "story",  label: "Story (9:16)",       ratio: "9 / 16",    source: "ig-story", icon: ImageIcon },
];

function ShareQRSection({ station, theme }) {
  const slug = station.slug || station.station_id;
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [posterFmt, setPosterFmt] = useState("a4");
  if (!slug) return null;

  const publicUrl = `${window.location.origin}/r/${slug}`;
  const qrSrcFlyer = `${API_BASE}/api/public/stations/by-slug/${encodeURIComponent(slug)}/qr.png?size=480&source=flyer`;
  const activeFormat = POSTER_FORMATS.find((f) => f.id === posterFmt) || POSTER_FORMATS[0];
  const posterPreviewSrc = `${API_BASE}/api/public/stations/by-slug/${encodeURIComponent(slug)}/poster.png?fmt=${activeFormat.id}&source=${activeFormat.source}-preview`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      await appPrompt("Copy this link:", publicUrl);
    }
  };

  const downloadBlob = async (key, url, filename) => {
    setDownloading(key);
    try {
      const r = await fetch(url);
      if (!r.ok) throw new Error("Download failed");
      const blob = await r.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
    } catch {
      // Silent fail — preview still works in-page
    } finally {
      setDownloading("");
    }
  };

  const downloadQR = () => downloadBlob(
    "qr",
    `${API_BASE}/api/public/stations/by-slug/${encodeURIComponent(slug)}/qr.png?size=1200&source=flyer`,
    `broadcastflow-${slug}-qr.png`,
  );

  const downloadPoster = (fmt) => downloadBlob(
    `poster-${fmt.id}`,
    `${API_BASE}/api/public/stations/by-slug/${encodeURIComponent(slug)}/poster.png?fmt=${fmt.id}&source=${fmt.source}`,
    `broadcastflow-${slug}-poster-${fmt.id}.png`,
  );

  return (
    <section
      className="max-w-7xl mx-auto px-6 py-12 sm:py-16"
      data-testid="public-station-qr-section"
    >
      <div className="flex items-center gap-3 mb-6">
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center"
          style={{ ...theme.tintStyle, color: theme.hex }}
        >
          <QrCode className="h-4 w-4" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a5068] font-semibold">// Share offline</div>
          <h2 className="text-xl font-semibold text-[#c8ccd8] tracking-tight">Scan to tune in</h2>
        </div>
      </div>

      {/* Raw QR + copy link row */}
      <div
        className="bg-[#0a0d14] rounded-2xl overflow-hidden grid sm:grid-cols-[auto_1fr] gap-0 items-stretch"
        style={{ border: "0.5px solid #1e2130" }}
      >
        <div
          className="p-6 sm:p-8 flex items-center justify-center"
          style={{ background: `radial-gradient(circle, ${theme.hex}1a 0%, transparent 70%)` }}
        >
          <div
            className="rounded-xl bg-white p-3 shadow-2xl"
            style={{ boxShadow: `0 0 36px -8px ${theme.hex}88` }}
          >
            <img
              src={qrSrcFlyer}
              alt={`QR code for ${station.name}`}
              className="block h-44 w-44 sm:h-52 sm:w-52 object-contain"
              data-testid="public-station-qr-img"
              loading="lazy"
            />
          </div>
        </div>

        <div className="p-6 sm:p-8 flex flex-col justify-center">
          <h3 className="text-lg font-semibold text-[#f0f2f8] leading-tight">
            Put the station in someone&apos;s pocket
          </h3>
          <p className="mt-2 text-sm text-[#6b7280] leading-relaxed max-w-prose">
            Print this code on flyers, merch, or business cards. Anyone with a phone can scan
            it to land on your live player — no app install required.
          </p>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={downloadQR}
              disabled={downloading === "qr"}
              data-testid="qr-download-png"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-medium border transition disabled:opacity-50"
              style={{
                ...theme.tintStyle,
                ...theme.borderStyle,
                borderWidth: "0.5px",
                borderStyle: "solid",
                color: theme.hex,
              }}
            >
              <Download className="h-3.5 w-3.5" />
              {downloading === "qr" ? "Preparing…" : "Download QR (1200px)"}
            </button>
            <button
              type="button"
              onClick={copyLink}
              data-testid="qr-copy-link"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-medium bg-[#0f1117] text-[#c8ccd8] hover:bg-[#161a24] border border-[#1e2130] transition"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-[#3ecf8e]" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>

          <div
            className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-[#4a5068] font-mono truncate max-w-full"
            data-testid="qr-public-url"
            title={publicUrl}
          >
            <ExternalLink className="h-3 w-3" />
            <span className="truncate">{publicUrl}</span>
          </div>
        </div>
      </div>

      {/* Branded poster generator */}
      <div
        className="mt-6 bg-[#0a0d14] rounded-2xl overflow-hidden grid lg:grid-cols-[1fr_1.25fr] gap-0 items-stretch"
        style={{ border: "0.5px solid #1e2130" }}
        data-testid="public-station-poster-block"
      >
        {/* Live preview — re-renders when format changes */}
        <div
          className="p-6 sm:p-8 flex items-center justify-center"
          style={{ background: `radial-gradient(circle at 30% 20%, ${theme.hex}1a 0%, transparent 70%), #0a0d14` }}
        >
          <div
            className="rounded-xl overflow-hidden shadow-2xl bg-[#0a0d14] transition-all duration-300"
            style={{
              aspectRatio: activeFormat.ratio,
              width: activeFormat.id === "story" ? "240px" : activeFormat.id === "square" ? "320px" : "320px",
              maxWidth: "100%",
              boxShadow: `0 16px 48px -12px ${theme.hex}aa`,
              border: `0.5px solid ${theme.hex}66`,
            }}
          >
            <img
              key={activeFormat.id}
              src={posterPreviewSrc}
              alt={`${activeFormat.label} share poster preview`}
              className="block w-full h-full object-contain"
              data-testid={`poster-preview-${activeFormat.id}`}
              loading="lazy"
            />
          </div>
        </div>

        {/* Format picker + downloads */}
        <div className="p-6 sm:p-8 flex flex-col justify-center">
          <div
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold mb-3 w-fit"
            style={{ ...theme.tintStyle, color: theme.hex, border: `0.5px solid ${theme.hex}66` }}
          >
            New · branded share poster
          </div>
          <h3 className="text-lg font-semibold text-[#f0f2f8] leading-tight">
            One-click flyer + social post
          </h3>
          <p className="mt-2 text-sm text-[#6b7280] leading-relaxed max-w-prose">
            We mint a print-ready PNG that wraps your QR with your logo, station name, tagline,
            and a big &ldquo;SCAN TO LISTEN&rdquo; CTA — already styled to your genre. Pick a format
            below; downloads work even when you&apos;re signed out.
          </p>

          {/* Format pills */}
          <div className="mt-5 flex items-center gap-2 flex-wrap" role="tablist" aria-label="Poster format">
            {POSTER_FORMATS.map((f) => {
              const Icon = f.icon;
              const isActive = f.id === posterFmt;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setPosterFmt(f.id)}
                  data-testid={`poster-fmt-${f.id}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border transition"
                  style={isActive ? {
                    ...theme.tintStyle,
                    color: theme.hex,
                    borderColor: `${theme.hex}88`,
                    borderWidth: "0.5px",
                    borderStyle: "solid",
                  } : {
                    backgroundColor: "#0f1117",
                    color: "#4a5068",
                    border: "0.5px solid #1e2130",
                  }}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {f.label}
                </button>
              );
            })}
          </div>

          {/* Download buttons — one per format so a station owner can grab them in batch */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            {POSTER_FORMATS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => downloadPoster(f)}
                disabled={downloading === `poster-${f.id}`}
                data-testid={`poster-download-${f.id}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-medium border transition disabled:opacity-50 ${
                  f.id === posterFmt ? "" : "hover:bg-[#161a24]"
                }`}
                style={f.id === posterFmt ? {
                  ...theme.tintStyle,
                  ...theme.borderStyle,
                  borderWidth: "0.5px",
                  borderStyle: "solid",
                  color: theme.hex,
                } : {
                  backgroundColor: "#0f1117",
                  color: "#c8ccd8",
                  border: "0.5px solid #1e2130",
                }}
              >
                <Download className="h-3.5 w-3.5" />
                {downloading === `poster-${f.id}` ? "Preparing…" : `Download ${f.label.split(" ")[0]}`}
              </button>
            ))}
          </div>

          <div className="mt-4 text-[11px] text-[#4a5068]">
            Tip: posters are 300dpi (A4) / 1080px (social) — high-res enough for any printer or feed.
          </div>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Footer — directories + social
// ─────────────────────────────────────────────────────────────────────────────
// Inline WhatsApp icon (lucide-react has no WhatsApp glyph)
const WhatsAppIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
  </svg>
);

const SOCIAL_ICONS = {
  facebook:  Facebook,
  instagram: Instagram,
  twitter:   Twitter,
  youtube:   Youtube,
  linkedin:  Linkedin,
  tiktok:    Music, // lucide doesn't ship a TikTok icon — use Music as a stand-in
  whatsapp:  WhatsAppIcon,
};

const DIRECTORY_LABELS = {
  tunein:          "TuneIn",
  live365:         "Live365",
  iheartradio:     "iHeartRadio",
  audacy:          "Audacy",
  mixcloud:        "Mixcloud",
  radiogarden:     "Radio Garden",
  radioking:       "Radioking",
  azuracast:       "AzuraCast",
  broadcast_radio: "Broadcast Radio",
};

function StationFooter({ station, theme }) {
  const directories = Object.entries(station.directories || {}).filter(([_, url]) => !!url);
  const socials = Object.entries(station.social || {}).filter(([_, url]) => !!url);

  return (
    <footer className="bg-[#0a0d14] mt-12" style={{ borderTop: "0.5px solid #1e2130" }} data-testid="public-station-footer">
      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Listen on */}
        {directories.length > 0 && (
          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a5068] font-semibold mb-3">// Listen on</div>
            <div className="flex items-center gap-2 flex-wrap">
              {directories.map(([key, url]) => (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`directory-${key}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium border transition"
                  style={{ ...theme.tintStyle, ...theme.borderStyle, borderWidth: "0.5px", borderStyle: "solid", color: theme.hex }}
                >
                  {DIRECTORY_LABELS[key] || key}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Social */}
        {socials.length > 0 && (
          <div className="mb-10">
            <div className="text-[10px] uppercase tracking-[0.16em] text-[#4a5068] font-semibold mb-3">// Follow</div>
            <div className="flex items-center gap-2 flex-wrap">
              {socials.map(([key, raw]) => {
                const Icon = SOCIAL_ICONS[key] || ExternalLink;
                // WhatsApp may be stored as a phone number — wrap in wa.me deeplink
                const href = key === "whatsapp" && !raw.startsWith("http")
                  ? `https://wa.me/${raw.replace(/[^0-9]/g, "")}`
                  : raw;
                return (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`social-${key}`}
                    title={key}
                    aria-label={key}
                    className="h-9 w-9 rounded-full flex items-center justify-center transition hover:scale-110"
                    style={{ ...theme.tintStyle, color: theme.hex, border: `0.5px solid ${theme.hex}66` }}
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {station.paypal_link && (
          <div className="pb-6" data-testid="public-paypal-block">
            <a href={station.paypal_link} target="_blank" rel="noreferrer" data-testid="public-paypal-btn"
              className="inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-xs font-bold text-black transition hover:opacity-90"
              style={{ background: "#ffc439" }}>
              Support {station.name} — tip on PayPal <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}

        {/* Sign-off */}
        <div className="flex items-center justify-between flex-wrap gap-3 pt-6" style={{ borderTop: "0.5px solid #1e2130" }}>
          <div className="text-[11px] text-[#4a5068]">
            © {new Date().getFullYear()} {station.name}. All rights reserved.
          </div>
          <Link to="/" className="text-[11px] text-[#4a5068] hover:text-[#c8ccd8] inline-flex items-center gap-1 transition" data-testid="footer-platform-link">
            Powered by BroadcastFlow AI <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      </div>
    </footer>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────
export default function PublicStation() {
  const { slug } = useParams();
  const [station, setStation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/api/public/stations/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (!r.ok) throw new Error(r.status === 404 ? "Station not found" : "Failed to load station");
        return r.json();
      })
      .then((data) => { if (!cancelled) setStation(data); })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const theme = useMemo(() => getStationTheme(station?.genre || ""), [station?.genre]);

  // Update document title for SEO + browser tab
  useEffect(() => {
    if (station?.name) document.title = `${station.name} — Listen Live`;
  }, [station?.name]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0d14] text-[#c8ccd8] flex items-center justify-center" data-testid="public-station-loading">
        <div className="text-center">
          <div className="h-10 w-10 mx-auto rounded-full border-2 border-[#1e2130] border-t-[#4a9eff] animate-spin" />
          <div className="mt-4 text-[12px] text-[#4a5068]">Loading station…</div>
        </div>
      </div>
    );
  }

  if (error || !station) {
    return (
      <div className="min-h-screen bg-[#0a0d14] text-[#c8ccd8] flex items-center justify-center px-6" data-testid="public-station-error">
        <div className="text-center max-w-md">
          <div className="h-14 w-14 mx-auto rounded-full bg-[#0d1e35] text-[#4a9eff] flex items-center justify-center mb-4">
            <Radio className="h-6 w-6" />
          </div>
          <div className="text-xl font-semibold">Station not found</div>
          <p className="mt-2 text-sm text-[#6b7280]">
            We couldn&apos;t find a station at <code className="text-[#a78bfa]">/r/{slug}</code>.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1a3a5c] hover:bg-[#1e4d7a] text-[#7ec8ff] border border-[#1e4d7a] text-[12px] font-medium transition"
            data-testid="public-station-error-home"
          >
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0d14] text-[#c8ccd8] font-sans antialiased" data-testid="public-station-page">
      <StationHeader station={station} theme={theme} />
      <Hero station={station} theme={theme} />
      <NewsSection items={station.news || []} theme={theme} />
      <PodcastsSection items={station.podcasts || []} theme={theme} />
      <ShareQRSection station={station} theme={theme} />
      <StationFooter station={station} theme={theme} />
    </div>
  );
}
